import { useEffect, useMemo, useRef, useState } from 'react';

interface LeaderboardEntry {
  platformUsername: string;
  platform: 'twitch' | 'youtube' | 'kick';
  gemLifetime: number;
  gemBalance: number;
}

interface Fields {
  title?: string;
  limit?: number;
  showPlatformIcon?: boolean;
  variant?: 'podium' | 'compact';
  accentColor?: string;
  showCrown?: boolean;
  showRankChange?: boolean;
}

const PLATFORM_COLORS: Record<LeaderboardEntry['platform'], string> = {
  twitch: '#9146ff',
  youtube: '#ff0000',
  kick: '#53fc18',
};

const PLATFORM_GLYPH: Record<LeaderboardEntry['platform'], string> = {
  twitch: 'T',
  youtube: 'Y',
  kick: 'K',
};

const TIER = {
  1: {
    gold: '#fbbf24',
    glow: 'rgba(251, 191, 36, 0.65)',
    accent: '#fde68a',
    label: 'CHAMPION',
    icon: '👑',
  },
  2: {
    gold: '#cbd5e1',
    glow: 'rgba(203, 213, 225, 0.50)',
    accent: '#e2e8f0',
    label: 'LEGEND',
    icon: '🥈',
  },
  3: {
    gold: '#d97706',
    glow: 'rgba(217, 119, 6, 0.50)',
    accent: '#fbbf24',
    label: 'ELITE',
    icon: '🥉',
  },
} as const;

function formatN(n: number): string {
  return n.toLocaleString();
}

/** Smoothly animates from `prev` → `next` over `durationMs`. Returns the
 *  current rounded value. Used to make the gem counter feel alive. */
function useTweenedNumber(target: number, durationMs = 800): number {
  const [value, setValue] = useState(target);
  const startRef = useRef(target);
  const startTimeRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === value) return;
    startRef.current = value;
    startTimeRef.current = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTimeRef.current) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(startRef.current + (target - startRef.current) * eased);
      setValue(v);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else rafRef.current = null;
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return value;
}

/**
 * Premium gem leaderboard. Polls /api/streamers/<id>/gems/leaderboard
 * every 60s and surfaces:
 *   - top 3 as podium cards (#1 larger, gold-tinted, crown + shimmer)
 *   - rank 4+ as compact rows
 *   - rank-change arrows when positions shift between polls
 *   - tween animation on every gem number that changes
 */
export function GemLeaderboardWidget({
  streamerId,
  fields,
}: {
  streamerId: string;
  fields: Fields;
}) {
  const limit = Math.max(1, Math.min(50, Number(fields.limit) || 8));
  const title = typeof fields.title === 'string' ? fields.title : 'Top Supporters';
  const showPlatformIcon = fields.showPlatformIcon !== false;
  const showCrown = fields.showCrown !== false;
  const showRankChange = fields.showRankChange !== false;
  const variant: 'podium' | 'compact' = fields.variant === 'compact' ? 'compact' : 'podium';
  const accentColor =
    typeof fields.accentColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(fields.accentColor)
      ? fields.accentColor
      : '#a855f7';

  const [rows, setRows] = useState<LeaderboardEntry[] | null>(null);
  // Map username+platform → previous rank, for rank-change arrows.
  const prevRanksRef = useRef<Map<string, number>>(new Map());
  const [rankChanges, setRankChanges] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (!streamerId) return;
    let cancelled = false;
    const load = () => {
      fetch(`/api/streamers/${streamerId}/gems/leaderboard?limit=${limit}`)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then((json: { leaderboard?: LeaderboardEntry[] }) => {
          if (cancelled) return;
          const next = json.leaderboard ?? [];
          // Compute rank deltas vs the previous fetch.
          const changes = new Map<string, number>();
          next.forEach((row, i) => {
            const key = `${row.platform}-${row.platformUsername}`;
            const prevRank = prevRanksRef.current.get(key);
            if (prevRank != null && prevRank !== i) {
              changes.set(key, prevRank - i); // positive = climbed
            }
          });
          // Refresh prev-rank map.
          const newMap = new Map<string, number>();
          next.forEach((row, i) => newMap.set(`${row.platform}-${row.platformUsername}`, i));
          prevRanksRef.current = newMap;
          setRows(next);
          setRankChanges(changes);
        })
        .catch((err) => {
          if (!cancelled) console.error('[gem-leaderboard] fetch failed', err);
        });
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [streamerId, limit]);

  const podium = useMemo(() => (rows ?? []).slice(0, 3), [rows]);
  const rest = useMemo(() => (rows ?? []).slice(3), [rows]);

  return (
    <>
      <style>{`
        @keyframes hv-gl-pop-in {
          0%   { transform: translateY(12px) scale(0.96); opacity: 0; }
          60%  { transform: translateY(0)    scale(1.02); opacity: 1; }
          100% { transform: translateY(0)    scale(1);    opacity: 1; }
        }
        @keyframes hv-gl-shimmer {
          0%   { transform: translateX(-100%); }
          60%  { transform: translateX(140%); }
          100% { transform: translateX(140%); }
        }
        @keyframes hv-gl-gem-spin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes hv-gl-gem-pulse {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50%      { transform: scale(1.08) rotate(8deg); }
        }
        @keyframes hv-gl-crown-bob {
          0%, 100% { transform: translateY(0) rotate(-8deg); }
          50%      { transform: translateY(-4px) rotate(8deg); }
        }
        @keyframes hv-gl-glow-pulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.08); }
        }
        @keyframes hv-gl-spark {
          0%   { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) scale(0); opacity: 0; }
        }

        .hv-gl-root {
          width: 100%;
          height: 100%;
          padding: 18px;
          box-sizing: border-box;
          background:
            radial-gradient(120% 80% at 20% 0%, rgba(168,85,247,0.22) 0%, transparent 60%),
            radial-gradient(120% 80% at 80% 100%, rgba(251,191,36,0.10) 0%, transparent 55%),
            linear-gradient(180deg, #0c0a1a 0%, #0a0617 100%);
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.05);
          box-shadow: 0 12px 40px rgba(168,85,247,0.18), inset 0 1px 0 rgba(255,255,255,0.06);
          color: #fff;
          font-family: 'Poppins', system-ui, -apple-system, 'Segoe UI', sans-serif;
          display: flex;
          flex-direction: column;
          gap: 14px;
          overflow: hidden;
          position: relative;
          animation: hv-gl-pop-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        /* subtle starfield */
        .hv-gl-root::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.4), transparent 50%),
            radial-gradient(1px 1px at 70% 70%, rgba(255,255,255,0.3), transparent 50%),
            radial-gradient(1.5px 1.5px at 40% 80%, rgba(255,255,255,0.25), transparent 50%),
            radial-gradient(1px 1px at 85% 15%, rgba(255,255,255,0.35), transparent 50%);
          pointer-events: none;
          opacity: 0.7;
        }

        .hv-gl-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          position: relative;
          z-index: 1;
        }
        .hv-gl-title {
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: #fff;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hv-gl-title-icon {
          font-size: 18px;
          display: inline-block;
          animation: hv-gl-gem-pulse 2.2s ease-in-out infinite;
          filter: drop-shadow(0 0 8px var(--hv-accent));
        }
        .hv-gl-title-sub {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
        }

        /* PODIUM CARDS — top 3 emphasis */
        .hv-gl-podium {
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: relative;
          z-index: 1;
        }
        .hv-gl-podium-card {
          position: relative;
          padding: 12px 14px;
          border-radius: 12px;
          background:
            linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%);
          border: 1px solid rgba(255,255,255,0.08);
          display: grid;
          grid-template-columns: 36px 1fr auto;
          align-items: center;
          gap: 12px;
          overflow: hidden;
        }
        .hv-gl-podium-card.tier-1 {
          background:
            linear-gradient(135deg, rgba(251,191,36,0.22) 0%, rgba(251,191,36,0.05) 100%);
          border-color: rgba(251,191,36,0.5);
          box-shadow: 0 0 28px rgba(251,191,36,0.32), inset 0 1px 0 rgba(255,255,255,0.15);
          padding: 16px 16px 16px 14px;
        }
        .hv-gl-podium-card.tier-2 {
          background:
            linear-gradient(135deg, rgba(203,213,225,0.18) 0%, rgba(203,213,225,0.03) 100%);
          border-color: rgba(203,213,225,0.4);
          box-shadow: 0 0 18px rgba(203,213,225,0.22);
        }
        .hv-gl-podium-card.tier-3 {
          background:
            linear-gradient(135deg, rgba(217,119,6,0.18) 0%, rgba(217,119,6,0.03) 100%);
          border-color: rgba(217,119,6,0.4);
          box-shadow: 0 0 18px rgba(217,119,6,0.22);
        }
        .hv-gl-podium-card.tier-1 .hv-gl-shimmer-pass {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%);
          transform: translateX(-100%);
          animation: hv-gl-shimmer 4.5s ease-in-out infinite;
          pointer-events: none;
        }
        .hv-gl-glow-halo {
          position: absolute;
          inset: -20% -10%;
          background: radial-gradient(50% 60% at 50% 50%, var(--hv-tier-glow) 0%, transparent 70%);
          pointer-events: none;
          animation: hv-gl-glow-pulse 3.2s ease-in-out infinite;
          z-index: 0;
        }

        .hv-gl-rank-cluster {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          position: relative;
          z-index: 1;
        }
        .hv-gl-medal {
          font-size: 22px;
          line-height: 1;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
        }
        .hv-gl-tier-1 .hv-gl-medal {
          font-size: 26px;
          animation: hv-gl-crown-bob 2.4s ease-in-out infinite;
        }
        .hv-gl-rank-num {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.5px;
          color: var(--hv-tier-color);
          text-shadow: 0 0 8px var(--hv-tier-glow);
        }

        .hv-gl-name-block {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
          position: relative;
          z-index: 1;
        }
        .hv-gl-name {
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          letter-spacing: 0.3px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hv-gl-tier-1 .hv-gl-name { font-size: 18px; }
        .hv-gl-tier-label {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: var(--hv-tier-color);
          opacity: 0.85;
          text-shadow: 0 0 6px var(--hv-tier-glow);
        }

        .hv-gl-platform-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border-radius: 5px;
          font-size: 10px;
          font-weight: 800;
          color: #fff;
          line-height: 1;
        }

        .hv-gl-rank-arrow {
          font-size: 10px;
          font-weight: 800;
          padding: 1px 5px;
          border-radius: 4px;
          line-height: 1.2;
          display: inline-flex;
          align-items: center;
          gap: 2px;
        }
        .hv-gl-rank-arrow.up   { background: rgba(34,197,94,0.18); color: #4ade80; }
        .hv-gl-rank-arrow.down { background: rgba(239,68,68,0.18); color: #f87171; }

        .hv-gl-amount-block {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0;
          position: relative;
          z-index: 1;
        }
        .hv-gl-amount {
          font-size: 20px;
          font-weight: 900;
          color: #fff;
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.5px;
          text-shadow: 0 0 12px var(--hv-tier-glow);
          line-height: 1;
          display: flex;
          align-items: baseline;
          gap: 5px;
        }
        .hv-gl-tier-1 .hv-gl-amount { font-size: 26px; }
        .hv-gl-amount-icon {
          font-size: 14px;
          animation: hv-gl-gem-spin 6s linear infinite;
          display: inline-block;
          filter: drop-shadow(0 0 6px var(--hv-tier-glow));
        }
        .hv-gl-amount-label {
          font-size: 8.5px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.5);
          margin-top: 2px;
        }

        /* COMPACT ROWS — rank 4+ */
        .hv-gl-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
          position: relative;
          z-index: 1;
        }
        .hv-gl-row {
          display: grid;
          grid-template-columns: 28px 1fr auto;
          align-items: center;
          gap: 10px;
          padding: 7px 12px;
          border-radius: 8px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.04);
          font-size: 13px;
        }
        .hv-gl-row-rank {
          color: var(--hv-accent);
          font-weight: 800;
          font-variant-numeric: tabular-nums;
          font-size: 14px;
        }
        .hv-gl-row-name {
          color: #e5e7eb;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hv-gl-row-amount {
          color: #fff;
          font-weight: 800;
          font-variant-numeric: tabular-nums;
          display: inline-flex;
          align-items: baseline;
          gap: 4px;
          font-size: 14px;
        }

        .hv-gl-empty {
          font-size: 12px;
          color: rgba(255,255,255,0.5);
          text-align: center;
          padding: 16px 8px;
          letter-spacing: 0.5px;
        }
      `}</style>

      <div
        className="hv-gl-root"
        style={{ ['--hv-accent' as string]: accentColor } as React.CSSProperties}
      >
        <div className="hv-gl-header">
          <div className="hv-gl-title">
            <span className="hv-gl-title-icon">💎</span>
            <span>{title}</span>
          </div>
          <div className="hv-gl-title-sub">Lifetime</div>
        </div>

        {rows === null ? (
          <div className="hv-gl-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="hv-gl-empty">No supporters yet — earn Gems by chatting!</div>
        ) : variant === 'compact' ? (
          <div className="hv-gl-list">
            {rows.map((r, i) => (
              <CompactRow
                key={`${r.platform}-${r.platformUsername}`}
                row={r}
                rank={i + 1}
                showPlatformIcon={showPlatformIcon}
                accentColor={accentColor}
                rankDelta={
                  showRankChange ? (rankChanges.get(`${r.platform}-${r.platformUsername}`) ?? 0) : 0
                }
              />
            ))}
          </div>
        ) : (
          <>
            <div className="hv-gl-podium">
              {podium.map((r, i) => (
                <PodiumCard
                  key={`${r.platform}-${r.platformUsername}`}
                  row={r}
                  rank={(i + 1) as 1 | 2 | 3}
                  showCrown={showCrown}
                  showPlatformIcon={showPlatformIcon}
                  rankDelta={
                    showRankChange
                      ? (rankChanges.get(`${r.platform}-${r.platformUsername}`) ?? 0)
                      : 0
                  }
                />
              ))}
            </div>
            {rest.length > 0 && (
              <div className="hv-gl-list">
                {rest.map((r, i) => (
                  <CompactRow
                    key={`${r.platform}-${r.platformUsername}`}
                    row={r}
                    rank={i + 4}
                    showPlatformIcon={showPlatformIcon}
                    accentColor={accentColor}
                    rankDelta={
                      showRankChange
                        ? (rankChanges.get(`${r.platform}-${r.platformUsername}`) ?? 0)
                        : 0
                    }
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function PodiumCard({
  row,
  rank,
  showCrown,
  showPlatformIcon,
  rankDelta,
}: {
  row: LeaderboardEntry;
  rank: 1 | 2 | 3;
  showCrown: boolean;
  showPlatformIcon: boolean;
  rankDelta: number;
}) {
  const tier = TIER[rank];
  const tweened = useTweenedNumber(row.gemLifetime);
  return (
    <div
      className={`hv-gl-podium-card tier-${rank} hv-gl-tier-${rank}`}
      style={
        {
          ['--hv-tier-color']: tier.gold,
          ['--hv-tier-glow']: tier.glow,
        } as React.CSSProperties
      }
    >
      {rank === 1 && <div className="hv-gl-glow-halo" />}
      {rank === 1 && <div className="hv-gl-shimmer-pass" />}
      <div className="hv-gl-rank-cluster">
        <span className="hv-gl-medal">{rank === 1 && showCrown ? tier.icon : tier.icon}</span>
        <span className="hv-gl-rank-num">#{rank}</span>
      </div>
      <div className="hv-gl-name-block">
        <div className="hv-gl-name">
          {showPlatformIcon && (
            <span
              className="hv-gl-platform-chip"
              style={{ background: PLATFORM_COLORS[row.platform] }}
              title={row.platform}
            >
              {PLATFORM_GLYPH[row.platform]}
            </span>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.platformUsername}
          </span>
          <RankDeltaBadge delta={rankDelta} />
        </div>
        <div className="hv-gl-tier-label">{tier.label}</div>
      </div>
      <div className="hv-gl-amount-block">
        <div className="hv-gl-amount">
          <span>{formatN(tweened)}</span>
          <span className="hv-gl-amount-icon">💎</span>
        </div>
        <div className="hv-gl-amount-label">Gems</div>
      </div>
    </div>
  );
}

function CompactRow({
  row,
  rank,
  showPlatformIcon,
  accentColor,
  rankDelta,
}: {
  row: LeaderboardEntry;
  rank: number;
  showPlatformIcon: boolean;
  accentColor: string;
  rankDelta: number;
}) {
  const tweened = useTweenedNumber(row.gemLifetime);
  return (
    <div className="hv-gl-row">
      <span className="hv-gl-row-rank" style={{ color: accentColor }}>
        #{rank}
      </span>
      <span className="hv-gl-row-name">
        {showPlatformIcon && (
          <span
            className="hv-gl-platform-chip"
            style={{ background: PLATFORM_COLORS[row.platform] }}
            title={row.platform}
          >
            {PLATFORM_GLYPH[row.platform]}
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.platformUsername}</span>
        <RankDeltaBadge delta={rankDelta} />
      </span>
      <span className="hv-gl-row-amount">
        {formatN(tweened)} <span aria-hidden>💎</span>
      </span>
    </div>
  );
}

function RankDeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) return null;
  if (delta > 0) {
    return (
      <span className="hv-gl-rank-arrow up" title={`Up ${delta}`}>
        ▲ {delta}
      </span>
    );
  }
  return (
    <span className="hv-gl-rank-arrow down" title={`Down ${-delta}`}>
      ▼ {-delta}
    </span>
  );
}
